/*
  Litmus buy box: keeps the price inside the add-to-cart label in step with the
  selected delivery option (Litmus cards or the Recharge widget) and variant.
  The server-rendered price is correct without JS; this only follows changes.
*/
if (!customElements.get('litmus-atc-price')) {
  customElements.define('litmus-atc-price', class extends HTMLElement {
    connectedCallback() {
      this.amount = this.querySelector('[data-litmus-atc-amount]');
      this.source = document.getElementById(`MainProduct-${this.dataset.section}`);
      this.form = document.getElementById(`product-form-${this.dataset.section}`);
      if (!this.amount || !this.source) return;
      this.readPrices();
      this.controller = new AbortController();
      this.schedule = () => {
        cancelAnimationFrame(this.frame);
        this.frame = requestAnimationFrame(() => this.update());
      };
      this.source.addEventListener('change', this.schedule, { signal: this.controller.signal });
      this.source.addEventListener('input', this.schedule, { signal: this.controller.signal });
      // Subscription apps can inject or rewrite a selling_plan input without firing events.
      if (this.form) {
        this.formObserver = new MutationObserver(this.schedule);
        this.formObserver.observe(this.form, {
          subtree: true, childList: true, attributes: true, attributeFilter: ['name', 'value', 'disabled'],
        });
      }
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          if (event?.data?.sectionId !== this.dataset.section) return;
          const next = event.data.html?.querySelector(`litmus-atc-price[data-section="${this.dataset.section}"] [data-litmus-atc-prices]`);
          if (next) this.readPrices(next);
          this.schedule();
        });
      }
      this.update();
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.formObserver?.disconnect();
      this.unsubscribe?.();
      cancelAnimationFrame(this.frame);
    }

    readPrices(script = this.querySelector('[data-litmus-atc-prices]')) {
      try {
        this.prices = JSON.parse(script?.textContent || 'null');
      } catch {
        this.prices = null;
      }
    }

    format(cents) {
      try {
        return new Intl.NumberFormat(document.documentElement.lang || 'en', {
          style: 'currency',
          currency: this.dataset.currency,
        }).format(cents / 100);
      } catch {
        return '';
      }
    }

    update() {
      const card = this.source.querySelector('.litmus-offer input:checked')?.closest('.litmus-offer');
      let text = card?.dataset.totalMoney || '';
      let unpriced = false;
      if (!text && this.form && this.prices) {
        const data = new FormData(this.form);
        const plan = String(data.get('selling_plan') || '');
        const quantity = Math.max(1, Number(data.get('quantity')) || 1);
        // A plan Shopify does not list for this variant (such as an app-only plan) cannot be priced
        // here; hide the amount rather than show the one-time price beside a subscription.
        unpriced = !!plan && this.prices.plans?.[plan] == null;
        const unit = plan && this.prices.plans?.[plan] != null ? this.prices.plans[plan] : this.prices.price;
        if (!unpriced && Number.isSafeInteger(unit)) text = this.format(unit * quantity);
      }
      this.toggleAttribute('data-unpriced', unpriced);
      if (text && this.amount.textContent !== text) this.amount.textContent = text;
    }
  });
}

/*
  When the Litmus delivery cards own a product form, they must be the only selling_plan
  source. The Recharge widget injects its own hidden selling_plan input inside the form;
  with both present the form posts two values (Recharge's last) and the sticky bar cannot
  read the plan. Its widget is already hidden by CSS in this case, so its input is
  renamed (kept in the DOM, excluded from submission).
*/
(() => {
  const guarded = new WeakSet();
  const neutralize = (form, own) => {
    form.querySelectorAll('[name="selling_plan"]').forEach((input) => {
      if (input === own || input.hasAttribute('data-litmus-plan-input')) return;
      input.setAttribute('data-litmus-neutralized', 'selling_plan');
      input.removeAttribute('name');
    });
  };
  const guard = () => {
    document.querySelectorAll('litmus-purchase-options [data-litmus-plan-input]').forEach((own) => {
      const form = own.form;
      if (!form) return;
      neutralize(form, own);
      if (guarded.has(form)) return;
      guarded.add(form);
      new MutationObserver(() => neutralize(form, own)).observe(form, {
        subtree: true, childList: true, attributes: true, attributeFilter: ['name'],
      });
    });
  };
  guard();
  document.addEventListener('DOMContentLoaded', guard);
  document.addEventListener('shopify:section:load', guard);
})();
